import i18n from '@link/share/i18n';
import { pushGa4AccountEvent } from './dataLayer';
import {
  endGa4AccountFlow,
  getGa4AccountEntry,
  markGa4AccountEventSent,
  setGa4PendingSignUp,
  takeGa4LogoutLanding,
  takeGa4PendingSignUp
} from './flow';
import { safely } from './safely';
import { getGa4SiteAgentId, whenGa4SiteAgentId } from './site';
import type {
  Ga4AccountAction,
  Ga4AccountAuthParams,
  Ga4AccountDataLayerEvent,
  Ga4AccountEventName,
  Ga4AuthMethod,
  Ga4YesNo
} from './types';

/** How long an event waits for the site schema before it is dropped. */
const AGENT_ID_WAIT_MS = 10_000;

/** One per flow: a repeat is a duplicate callback, never a new result. */
const SUCCESS_EVENTS: ReadonlySet<Ga4AccountEventName> = new Set([
  'sign_up',
  'email_verification_complete',
  'login',
  'profile_complete'
]);

// Event name and reason only: never a value that could identify the user.
function skip(event: Ga4AccountEventName, reason: string): void {
  console.warn(`[ga4] ${event} not sent: ${reason}`);
}

function send(
  event: Ga4AccountEventName,
  auth: Ga4AccountAuthParams = {}
): void {
  safely(() => {
    const entry = getGa4AccountEntry();
    if (!entry) {
      skip(event, 'no flow context');
      return;
    }
    const uiLanguage = i18n.language ?? '';
    if (!uiLanguage) {
      skip(event, 'no ui_language');
      return;
    }
    if (SUCCESS_EVENTS.has(event) && !markGa4AccountEventSent(event)) return;

    // Snapshot now: the language or the flow may change while agent_id is
    // pending.
    const payload: Omit<Ga4AccountDataLayerEvent, 'agent_id'> = {
      event,
      ui_language: uiLanguage,
      // Only the checkout gate starts a checkout flow.
      flow_context: entry === 'checkout_gate' ? 'checkout' : 'standalone',
      entry_point: entry,
      ...auth
    };
    const push = (agentId: string) =>
      safely(() => pushGa4AccountEvent({ ...payload, agent_id: agentId }));

    const agentId = getGa4SiteAgentId();
    if (agentId) {
      push(agentId);
      return;
    }
    const timeout = new Promise<null>(resolve => {
      window.setTimeout(() => resolve(null), AGENT_ID_WAIT_MS);
    });
    void Promise.race([whenGa4SiteAgentId(), timeout]).then(id => {
      if (id) push(id);
      else skip(event, 'site agent_id unavailable');
    });
  });
}

/** Params of the two email-code events: the code is required by definition. */
const emailCodeParams = (
  accountAction: Ga4AccountAction
): Ga4AccountAuthParams => ({
  method: 'email',
  account_action: accountAction,
  email_verification_required: 'yes'
});

/**
 * The login view is on screen with its method buttons. Not the one a logout
 * lands on; its flow still starts, so a sign-in from there counts.
 */
export function trackGa4ViewLogin(): void {
  if (takeGa4LogoutLanding()) return;
  send('view_login');
}

/** The requirement is left out only when it can no longer be known. */
function sendSignUp(
  method: Ga4AuthMethod,
  emailVerificationRequired?: Ga4YesNo
): void {
  send('sign_up', {
    method,
    account_action: 'created',
    ...(emailVerificationRequired
      ? { email_verification_required: emailVerificationRequired }
      : {})
  });
}

/**
 * Firebase created the account and whether a code is required is already
 * known (a social sign-in needs none). When it is not, use deferGa4SignUp.
 */
export function trackGa4SignUp(
  method: Ga4AuthMethod,
  emailVerificationRequired: Ga4YesNo
): void {
  sendSignUp(method, emailVerificationRequired);
}

/**
 * Firebase created the account, but whether a code is required is known only
 * later (email sign-up). The flow holds sign_up until settleGa4SignUp or the
 * flow's login sends it, whatever route the sign-in takes in between.
 */
export function deferGa4SignUp(method: Ga4AuthMethod): void {
  safely(() => {
    if (!setGa4PendingSignUp(method)) skip('sign_up', 'no flow context');
  });
}

/**
 * Send the sign_up this flow holds, once, now that the code requirement is
 * known; without it when it never will be. A no-op when none is held, so a
 * page revisited through an earlier flow's history entry sends nothing.
 */
export function settleGa4SignUp(emailVerificationRequired?: Ga4YesNo): void {
  safely(() => {
    const method = takeGa4PendingSignUp();
    if (method) sendSignUp(method, emailVerificationRequired);
  });
}

/** The email code form is on screen. */
export function trackGa4ViewEmailVerification(
  accountAction: Ga4AccountAction
): void {
  send('view_email_verification', emailCodeParams(accountAction));
}

/** The backend accepted the email code. */
export function trackGa4EmailVerificationComplete(
  accountAction: Ga4AccountAction
): void {
  send('email_verification_complete', emailCodeParams(accountAction));
}

/**
 * ARS tokens issued after every step this sign-in required. A sign_up the flow
 * still holds goes first: this sign-in answers its code requirement too.
 */
export function trackGa4Login(
  method: Ga4AuthMethod,
  accountAction: Ga4AccountAction,
  emailVerificationRequired: Ga4YesNo
): void {
  settleGa4SignUp(emailVerificationRequired);
  send('login', {
    method,
    account_action: accountAction,
    email_verification_required: emailVerificationRequired
  });
}

/** The profile form is on screen in completion mode. */
export function trackGa4ViewUserInfo(): void {
  send('view_user_info');
}

/** The saved profile is complete, which ends the flow. */
export function trackGa4ProfileComplete(): void {
  send('profile_complete');
  endGa4AccountFlow();
}
